import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  AlertTitle,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// New Jersey counties with major cities
const NJ_COUNTIES_DATA = [
  {
    name: 'Atlantic',
    cities: ['Atlantic City', 'Pleasantville', 'Ventnor City', 'Margate City', 'Egg Harbor Township'],
    region: 'South'
  },
  {
    name: 'Bergen',
    cities: ['Hackensack', 'Fort Lee', 'Paramus', 'Teaneck', 'Englewood'],
    region: 'North'
  },
  {
    name: 'Burlington',
    cities: ['Burlington', 'Mount Holly', 'Willingboro', 'Moorestown', 'Evesham'],
    region: 'South'
  },
  {
    name: 'Camden',
    cities: ['Camden', 'Cherry Hill', 'Gloucester Township', 'Pennsauken', 'Voorhees'],
    region: 'South'
  },
  {
    name: 'Cape May',
    cities: ['Cape May', 'Wildwood', 'Ocean City', 'North Wildwood', 'Sea Isle City'],
    region: 'South'
  },
  {
    name: 'Cumberland',
    cities: ['Bridgeton', 'Vineland', 'Millville', 'Deerfield', 'Commercial'],
    region: 'South'
  },
  {
    name: 'Essex',
    cities: ['Newark', 'East Orange', 'Irvington', 'Orange', 'Bloomfield'],
    region: 'North'
  },
  {
    name: 'Gloucester',
    cities: ['Glassboro', 'Deptford', 'Washington Township', 'Monroe Township', 'Woodbury'],
    region: 'South'
  },
  {
    name: 'Hudson',
    cities: ['Jersey City', 'Hoboken', 'Union City', 'West New York', 'Bayonne'],
    region: 'North'
  },
  {
    name: 'Hunterdon',
    cities: ['Flemington', 'Clinton', 'Lebanon', 'Raritan Township', 'Readington'],
    region: 'Central'
  },
  {
    name: 'Mercer',
    cities: ['Trenton', 'Princeton', 'Hamilton', 'Lawrence', 'Ewing'],
    region: 'Central'
  },
  {
    name: 'Middlesex',
    cities: ['New Brunswick', 'Edison', 'Woodbridge', 'Perth Amboy', 'Piscataway'],
    region: 'Central'
  },
  {
    name: 'Monmouth',
    cities: ['Freehold', 'Long Branch', 'Asbury Park', 'Red Bank', 'Neptune'],
    region: 'Central'
  },
  {
    name: 'Morris',
    cities: ['Morristown', 'Dover', 'Parsippany', 'Randolph', 'Madison'],
    region: 'North'
  },
  {
    name: 'Ocean',
    cities: ['Toms River', 'Lakewood', 'Brick', 'Jackson', 'Point Pleasant'],
    region: 'Central'
  },
  {
    name: 'Passaic',
    cities: ['Paterson', 'Passaic', 'Clifton', 'Wayne', 'West Milford'],
    region: 'North'
  },
  {
    name: 'Salem',
    cities: ['Salem', 'Pennsville', 'Carneys Point', 'Woodstown', 'Pittsgrove'],
    region: 'South'
  },
  {
    name: 'Somerset',
    cities: ['Somerville', 'Bridgewater', 'Franklin Township', 'Hillsborough', 'Bound Brook'],
    region: 'Central'
  },
  {
    name: 'Sussex',
    cities: ['Newton', 'Vernon', 'Sparta', 'Hopatcong', 'Franklin'],
    region: 'North'
  },
  {
    name: 'Union',
    cities: ['Elizabeth', 'Plainfield', 'Union', 'Linden', 'Rahway'],
    region: 'North'
  },
  {
    name: 'Warren',
    cities: ['Phillipsburg', 'Washington', 'Hackettstown', 'Belvidere', 'Lopatcong'],
    region: 'North'
  }
];

const NJCountiesMap = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('All');

  // Filter counties based on search and region
  const filteredCounties = NJ_COUNTIES_DATA.filter(county => {
    const matchesSearch = 
      county.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      county.cities.some(city => city.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRegion = selectedRegion === 'All' || county.region === selectedRegion;
    
    return matchesSearch && matchesRegion;
  });

  // Get unique regions
  const regions = ['All', ...new Set(NJ_COUNTIES_DATA.map(c => c.region))];

  const handleCountyClick = (county) => {
    // For now, just show an alert. Later this can navigate to county-specific page
    console.log('Selected county:', county.name);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 4, 
            background: 'linear-gradient(135deg, #1B5E20 0%, #4C8C4A 100%)',
            color: 'white',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LocationIcon sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h3" fontWeight="bold">
              New Jersey Counties & Cities
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Explore foreclosure opportunities across New Jersey's 21 counties
          </Typography>
        </Paper>

        {/* Coming Soon Alert */}
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ mb: 4 }}
        >
          <AlertTitle>Coming Soon!</AlertTitle>
          We're currently expanding our foreclosure listings to cover all New Jersey counties. 
          Full access to properties, dashboards, and analytics will be available soon. 
          Stay tuned for updates!
        </Alert>

        {/* Search and Filter */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search counties or cities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {regions.map(region => (
                  <Chip
                    key={region}
                    label={region}
                    onClick={() => setSelectedRegion(region)}
                    color={selectedRegion === region ? 'primary' : 'default'}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: selectedRegion === region ? '#1B5E20' : undefined,
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Counties Grid */}
        <Grid container spacing={3}>
          {filteredCounties.map((county) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={county.name}>
              <Card 
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => handleCountyClick(county)}
                  sx={{ height: '100%', p: 0 }}
                >
                  <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold" 
                        color="primary"
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <LocationIcon sx={{ mr: 1 }} />
                        {county.name} County
                      </Typography>
                      <Chip 
                        label={`${county.region} NJ`} 
                        size="small" 
                        sx={{ mt: 1 }}
                        color="secondary"
                      />
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      Major Cities:
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      {county.cities.slice(0, 5).map((city, index) => (
                        <Typography 
                          key={index}
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            pl: 2, 
                            py: 0.5,
                            '&::before': {
                              content: '"•"',
                              mr: 1,
                              color: 'primary.main'
                            }
                          }}
                        >
                          {city}
                        </Typography>
                      ))}
                    </Box>
                    
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        Click to explore (Coming Soon)
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* No Results */}
        {filteredCounties.length === 0 && (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No counties or cities found matching your search
            </Typography>
          </Paper>
        )}

        {/* Info Section */}
        <Paper elevation={2} sx={{ p: 4, mt: 4, backgroundColor: '#f9fafb' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            About New Jersey Foreclosures
          </Typography>
          <Typography variant="body1" paragraph>
            New Jersey has 21 counties, each with unique real estate markets and foreclosure opportunities. 
            Our platform is designed to help investors discover and analyze foreclosure properties across 
            the entire state.
          </Typography>
          <Typography variant="body1">
            We're currently building comprehensive coverage for all counties. Check back soon for 
            detailed property listings, market analytics, and investment tools for each region.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default NJCountiesMap;


